import { GAUGE_GEOMETRY, MAX_SCORE, RISK_BAND_RANGES } from '../constants';
import { ERiskBand } from '../types';

export interface IGaugePointer {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface IGaugeArc {
  band: ERiskBand;
  d: string;
}

/** Ordem de desenho: do pior score (inicio do arco) para o melhor. */
const BAND_DRAW_ORDER: Array<ERiskBand> = [ERiskBand.HIGH, ERiskBand.MEDIUM, ERiskBand.LOW];

/**
 * Angulo do ponteiro para um score, em graus. Escala linear sobre o arco de
 * {@link GAUGE_GEOMETRY.sweepAngle} graus: score 0 no inicio, {@link MAX_SCORE} no fim.
 */
export function gaugeAngleForScore(score: number): number {
  const clampedScore = Math.max(0, Math.min(MAX_SCORE, score));
  return GAUGE_GEOMETRY.startAngle - (clampedScore / MAX_SCORE) * GAUGE_GEOMETRY.sweepAngle;
}

export function calculateGaugePointer(score: number): IGaugePointer {
  const angleRadians = (gaugeAngleForScore(score) * Math.PI) / 180;

  return {
    x1: GAUGE_GEOMETRY.centerX,
    y1: GAUGE_GEOMETRY.centerY,
    x2: GAUGE_GEOMETRY.centerX + GAUGE_GEOMETRY.pointerLength * Math.cos(angleRadians),
    y2: GAUGE_GEOMETRY.centerY - GAUGE_GEOMETRY.pointerLength * Math.sin(angleRadians),
  };
}

/**
 * Arcos coloridos do gauge, um por faixa de risco
 */
export function buildGaugeArcs(): Array<IGaugeArc> {
  let arcStartScore = 0;

  return BAND_DRAW_ORDER.map(band => {
    const arcEndScore = RISK_BAND_RANGES[band].max;
    const arc: IGaugeArc = {
      band,
      d: ringSegmentPath(gaugeAngleForScore(arcStartScore), gaugeAngleForScore(arcEndScore)),
    };
    arcStartScore = arcEndScore;
    return arc;
  });
}

/** Segmento de anel: arco externo de ida, arco interno de volta. */
function ringSegmentPath(fromAngle: number, toAngle: number): string {
  const { outerRadius, innerRadius } = GAUGE_GEOMETRY;
  const outerStart = polarToCartesian(outerRadius, fromAngle);
  const outerEnd = polarToCartesian(outerRadius, toAngle);
  const innerEnd = polarToCartesian(innerRadius, toAngle);
  const innerStart = polarToCartesian(innerRadius, fromAngle);
  const largeArc = Math.abs(fromAngle - toAngle) > 180 ? 1 : 0;

  return [
    `M${outerStart.x} ${outerStart.y}`,
    `A${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L${innerEnd.x} ${innerEnd.y}`,
    `A${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join('');
}

function polarToCartesian(radius: number, angleDegrees: number): { x: number; y: number } {
  const angleRadians = (angleDegrees * Math.PI) / 180;
  return {
    x: round(GAUGE_GEOMETRY.centerX + radius * Math.cos(angleRadians)),
    y: round(GAUGE_GEOMETRY.centerY - radius * Math.sin(angleRadians)),
  };
}

function round(value: number): number {
  return Math.round(value * 1000) / 1000;
}
